"use client";

import { Suspense, useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import LoginContent from "@/components/sections/auth/LoginContent";
import OAuthCallbackHandler from "@/components/auth/OAuthCallbackHandler";
import { isAuthenticated } from "@/lib/auth";

function LoginPageContent() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [isChecking, setIsChecking] = useState(true);
	const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
	const showUserNotFoundError =
		searchParams.get("error") === "user_not_found";

	// Check if user is already authenticated on mount and on reload
	useEffect(() => {
		const checkAuth = async () => {
			try {
				// Stale Cognito session + DB missing user: never bounce back to /auth/redirect
				if (searchParams.get("error") === "user_not_found") {
					const { signOutUser } = await import("@/services/cognito");
					await signOutUser();
					setIsAuthenticatedState(false);
					setIsChecking(false);
					return;
				}

				const authenticated = await isAuthenticated();
				if (authenticated) {
					// User is authenticated, check for action=checkout query parameter
					const actionCheckout =
						searchParams.get("action") === "checkout";
					setIsAuthenticatedState(true);

					// Build redirect URL with action=checkout query parameter if present
					let redirectUrl = "/auth/redirect";
					if (actionCheckout) {
						redirectUrl = "/auth/redirect?action=checkout";
						console.log(
							"LoginPage - User is authenticated with action=checkout, redirecting to /auth/redirect with action=checkout"
						);
					} else {
						console.log(
							"LoginPage - User is authenticated, redirecting to /auth/redirect"
						);
					}

					router.push(redirectUrl);
					return;
				}
				// User is not authenticated, show login page
				setIsAuthenticatedState(false);
				setIsChecking(false);
			} catch (error) {
				console.error("LoginPage - Error checking auth:", error);
				// On error, assume not authenticated and show login page
				setIsAuthenticatedState(false);
				setIsChecking(false);
			}
		};

		checkAuth();
	}, [router, searchParams]);

	// Also check periodically in case auth state changes (e.g., user logs in another tab)
	useEffect(() => {
		if (isAuthenticatedState) {
			return; // Already authenticated, no need to check again
		}

		const interval = setInterval(async () => {
			try {
				const authenticated = await isAuthenticated();
				if (authenticated && !isAuthenticatedState) {
					// Check for action=checkout query parameter
					const actionCheckout =
						searchParams.get("action") === "checkout";
					setIsAuthenticatedState(true);

					// Build redirect URL with action=checkout query parameter if present
					let redirectUrl = "/auth/redirect";
					if (actionCheckout) {
						redirectUrl = "/auth/redirect?action=checkout";
						console.log(
							"LoginPage - Auth state changed with action=checkout, redirecting to /auth/redirect with action=checkout"
						);
					} else {
						console.log(
							"LoginPage - Auth state changed, redirecting to /auth/redirect"
						);
					}

					router.push(redirectUrl);
				}
			} catch (error) {
				// Ignore errors in periodic check
			}
		}, 1000); // Check every second

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router, isAuthenticatedState]); // Check searchParams inside the interval

	// Show loading state while checking auth or if authenticated (redirecting)
	if (isChecking || isAuthenticatedState) {
		return (
			<>
				<div className="min-h-screen bg-[#16251c] flex items-center justify-center">
					<div className="text-center">
						<div className="w-12 h-12 border-4 border-[#A7FF5E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<p className="text-[#edffde] text-sm">Loading...</p>
					</div>
				</div>
				<OAuthCallbackHandler
					checkAuth={() => {}}
					setIsChecking={setIsChecking}
				/>
			</>
		);
	}

	// Show user not found error
	if (showUserNotFoundError) {
		return (
			<div className="min-h-screen bg-[#16251c] flex items-center justify-center">
				<div className="text-center max-w-md px-4">
					<div className="w-12 h-12 border-4 border-red-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
					<div className="text-red-400 text-xl font-semibold mb-4">
						Account Error
					</div>
					<p className="text-[#edffde] text-sm mb-2">
						There is something wrong with your account
					</p>
					<p className="text-[#8c99a7] text-xs mb-6">
						Please contact support
					</p>
					<p className="text-[#8c99a7] text-xs">
						You have been logged out automatically
					</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<OAuthCallbackHandler
				checkAuth={() => {}}
				setIsChecking={setIsChecking}
			/>
			<LoginContent />
		</>
	);
}

export default function LoginPage() {
	return (
		<Suspense
			fallback={
				<div className="min-h-screen bg-[#16251c] flex items-center justify-center">
					<div className="text-center">
						<div className="w-12 h-12 border-4 border-[#A7FF5E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<p className="text-[#edffde] text-sm">Loading...</p>
					</div>
				</div>
			}
		>
			<LoginPageContent />
		</Suspense>
	);
}
